/**
 * Single source of the admin's destinations (AD-016/AD-019), shared by the
 * desktop sidebar, the mobile tab bar and the Mais sheet. URLs are unchanged.
 */

export type NavItemId =
  | 'home'
  | 'campaigns'
  | 'analytics'
  | 'app' // tabs
  | 'audiences'
  | 'team'
  | 'billing'
  | 'settings'; // "Loja" group

export interface NavContext {
  tenantSlug: string;
  audiencesEnabled: boolean;
  hasActiveSubscription: boolean;
  fromShopifyAppStore: boolean;
}

export interface NavItem {
  id: NavItemId;
  href: string;
  group: 'primary' | 'store';
  soonTag: boolean;
}

export const TAB_BAR_IDS = ['home', 'campaigns', 'analytics', 'app'] as const;

const PATHS: Record<NavItemId, string> = {
  home: 'dashboard',
  campaigns: 'campaigns',
  analytics: 'metrics',
  app: 'customization',
  audiences: 'audiences',
  team: 'team',
  billing: 'billing',
  settings: 'settings',
};

function item(
  id: NavItemId,
  tenantSlug: string,
  group: NavItem['group'],
  soonTag = false
): NavItem {
  return { id, href: `/${tenantSlug}/${PATHS[id]}`, group, soonTag };
}

/** Order: home, campaigns, analytics, app, audiences, team, billing?, settings?. */
export function getNavItems(ctx: NavContext): NavItem[] {
  const slug = ctx.tenantSlug;
  return [
    ...getTabBarItems(slug),
    item('audiences', slug, 'store', !ctx.audiencesEnabled),
    item('team', slug, 'store'),
    ...(ctx.hasActiveSubscription ? [item('billing', slug, 'store')] : []),
    ...(ctx.fromShopifyAppStore ? [item('settings', slug, 'store')] : []),
  ];
}

/** The 4 tabs (Mais is a button, not a destination). */
export function getTabBarItems(tenantSlug: string): NavItem[] {
  return TAB_BAR_IDS.map((id) => item(id, tenantSlug, 'primary'));
}

/** Destinations of the Mais sheet: audiences, team, billing?, settings?. */
export function getMoreItems(ctx: NavContext): NavItem[] {
  const tabIds: readonly NavItemId[] = TAB_BAR_IDS;
  return getNavItems(ctx).filter((i) => !tabIds.includes(i.id));
}

export function isNavItemActive(href: string, pathname: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/** Whether the current route is one of the Mais destinations. */
export function isMoreSectionActive(
  ctx: NavContext,
  pathname: string
): boolean {
  return getMoreItems(ctx).some((i) => isNavItemActive(i.href, pathname));
}

/**
 * "Novidades" (AD-046): in the sidebar footer and the Mais sheet, outside the
 * tab bar and the destinations above.
 */
export function whatsNewHref(tenantSlug: string): string {
  return `/${tenantSlug}/whats-new`;
}
